import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import { Clock, Target, Users, TrendingDown } from "lucide-react";

interface StatProps {
    icon: React.ElementType;
    value: number | string;
    suffix: string;
    prefix?: string;
    label: string;
    accent: string;
}

const AnimatedCounter = ({ value, suffix, prefix = "" }: { value: number; suffix: string; prefix?: string }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => `${prefix}${Math.round(latest)}${suffix}`);
    const ref = useRef<HTMLSpanElement>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    animate(count, value, { duration: 2, ease: "easeOut" });
                }
            },
            { threshold: 0.5 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [count, value]);

    return <motion.span ref={ref}>{rounded}</motion.span>;
};

const StatCard = ({ icon: Icon, value, suffix, prefix, label, accent }: StatProps) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center group"
    >
        <div className={`w-14 h-14 rounded-2xl ${accent} flex items-center justify-center mx-auto mb-5 shadow-sm group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-7 h-7" />
        </div>
        <p className="font-display text-5xl md:text-6xl font-bold text-slate-900 mb-3 tracking-tight">
            {typeof value === 'number' ? (
                <AnimatedCounter value={value} suffix={suffix} prefix={prefix} />
            ) : (
                <span>{value}</span>
            )}
        </p>
        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
    </motion.div>
);

const stats: StatProps[] = [
    { icon: Clock, value: 80, suffix: "%", label: "Screening Time Saved", accent: "bg-accent/10 text-accent" },
    { icon: Target, value: 10, suffix: "x", label: "Matching Precision", accent: "bg-blue-500/10 text-blue-500" },
    { icon: Users, value: "7+", suffix: "", label: "Hiring Teams", accent: "bg-emerald-500/10 text-emerald-500" },
    { icon: TrendingDown, value: 12, suffix: "", prefix: "", label: "Days Avg Time-to-Hire", accent: "bg-amber-500/10 text-amber-500" },
];

const StatsHighlightSection = () => {
    return (
        <section className="py-24 bg-white relative overflow-hidden border-y border-slate-100">
            <div className="absolute top-0 left-[40%] w-[500px] h-[500px] bg-accent/3 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-12 md:gap-8 max-w-5xl mx-auto">
                    {stats.map((stat) => (
                        <StatCard key={stat.label} {...stat} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StatsHighlightSection;
